/**
 * Department Details with Tabbed Interface Component
 * 
 * Displays department information with separate tabs for Users and Providers
 * matching the provided UI design.
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { Typography, Skeleton, Empty } from 'antd';
import { Box, Tabs, Tab, IconButton } from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
// import { Edit as EditIcon } from '@mui/icons-material'; // TODO: Re-import when action columns are re-enabled
import { useSearchParams } from 'react-router-dom';
import StickyFlexTable from '../common/StickyFlexTable';
import ThemedChip from '../common/ThemedChip';
import LogoLoader from '../common/loaders/gif-logo-loader';
import StaffSelectionModal from './StaffSelectionModal';
import ProviderSelectionModal from './ProviderSelectionModal';
import { useTheme } from '../../ThemeContext';
import DepartmentService from '../../services/department-service';

const { Title, Text } = Typography;

const DepartmentDetailsTabbed = ({ 
  department, 
  facility,
  facilityId,
  loading, 
  onClose, 
  onEdit 
}) => {
  const { themeMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State for tab management and data loading
  const [activeTab, setActiveTab] = useState(0);
  const [staff, setStaff] = useState([]);
  const [providers, setProviders] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);

  // Initialize tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'providers') {
      setActiveTab(1);
    } else {
      setActiveTab(0); // Default to users tab
    }
  }, [searchParams]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Update URL with tab parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 1) {
      newSearchParams.set('tab', 'providers');
    } else {
      newSearchParams.set('tab', 'users');
    }
    setSearchParams(newSearchParams);
  };

  // Load data based on active tab and department ID
  useEffect(() => {
    const loadTabData = async () => {
      if (!department?._id) return;
      
      setDataLoading(true);
      console.log(`🔄 Loading ${activeTab === 0 ? 'staff' : 'providers'} for department:`, department._id);
      
      try {
        if (activeTab === 0) {
          // Load Users/Staff (only users with role 'user')
          const result = await DepartmentService.getStaffByDepartment(department._id);
          if (result.success) {
            // Filter to only include users with role 'user'
            const filteredUsers = result.data.filter(user => 
              Array.isArray(user.role) && user.role.includes('user')
            );
            console.log('✅ Users loaded:', filteredUsers.length, 'users (filtered from', result.data.length, 'total staff)');
            console.log('🔍 Sample user data:', filteredUsers[0]); // Debug first user
            setStaff(filteredUsers);
          } else {
            console.warn('⚠️ Failed to load staff:', result.message);
            setStaff([]);
          }
        } else {
          // Load Providers
          const result = await DepartmentService.getProvidersByDepartment(department._id);
          if (result.success) {
            console.log('✅ Providers loaded:', result.data.length, 'providers');
            console.log('🔍 Sample provider data:', result.data[0]); // Debug first provider
            setProviders(result.data);
          } else {
            console.warn('⚠️ Failed to load providers:', result.message);
            setProviders([]);
          }
        }
      } catch (error) {
        console.error('❌ Error loading tab data:', error);
        if (activeTab === 0) {
          setStaff([]);
        } else {
          setProviders([]);
        }
      } finally {
        setDataLoading(false);
      }
    };

    loadTabData();
  }, [department?._id, activeTab]);

  /**
   * Handles opening staff selection modal
   */
  const handleAddStaffClick = () => {
    if (!department?._id || !facilityId) {
      console.warn('Missing department ID or facility ID');
      return;
    }
    setStaffModalOpen(true);
  };

  /**
   * Handles closing staff selection modal
   */
  const handleStaffModalClose = () => {
    setStaffModalOpen(false);
  };

  /**
   * Handles successful staff addition - refreshes the Users tab data
   */
  const handleStaffModalSave = () => {
    console.log('🔄 Refreshing staff data after addition');
    // Reload the users tab data to show newly added staff
    if (activeTab === 0 && department?._id) {
      const refreshUsers = async () => {
        setDataLoading(true);
        try {
          const result = await DepartmentService.getStaffByDepartment(department._id);
          if (result.success) {
            const filteredUsers = result.data.filter(user => 
              Array.isArray(user.role) && user.role.includes('user')
            );
            setStaff(filteredUsers);
            console.log('✅ Staff data refreshed after addition:', filteredUsers.length);
          }
        } catch (error) {
          console.error('❌ Error refreshing staff data:', error);
        } finally {
          setDataLoading(false);
        }
      };
      refreshUsers();
    }
  };

  /**
   * Handles opening provider selection modal
   */
  const handleAddProviderClick = () => {
    if (!department?._id || !facilityId) {
      console.warn('Missing department ID or facility ID');
      return;
    }
    
    console.log('🔍 Debug - Opening provider modal:', {
      departmentId: department?._id,
      facilityId: facilityId,
      currentProviders: providers.map(p => ({ id: p._id, name: p.fullName })),
      currentProviderIds: currentProviderIds
    });
    
    setProviderModalOpen(true);
  };

  /**
   * Handles closing provider selection modal
   */
  const handleProviderModalClose = () => {
    setProviderModalOpen(false);
  };

  /**
   * Handles successful provider addition - refreshes the Providers tab data
   */
  const handleProviderModalSave = () => {
    console.log('🔄 Refreshing provider data after addition');
    setProviderModalOpen(false);
    // Reload the providers tab data to show newly added providers
    if (activeTab === 1 && department?._id) {
      const refreshProviders = async () => {
        setDataLoading(true);
        try {
          const result = await DepartmentService.getProvidersByDepartment(department._id);
          if (result.success) {
            setProviders(result.data);
            console.log('✅ Provider data refreshed after addition:', result.data.length);
          }
        } catch (error) {
          console.error('❌ Error refreshing provider data:', error);
        } finally {
          setDataLoading(false);
        }
      };
      refreshProviders();
    }
  };

  // Get current staff IDs for the modal (to exclude them from selection)
  const currentStaffIds = staff.map(s => s._id);
  
  // Get current provider IDs for the modal (to exclude them from selection) 
  const currentProviderIds = providers.map(p => p._id);

  // User table columns for StickyFlexTable (only showing users with role 'user')
  const userColumns = [
    {
      renderName: 'name',
      headerName: 'Name',
      minWidth: 150,
    },
    {
      renderName: 'phoneNumber',
      headerName: 'Phone Number',
      minWidth: 140,
      renderCell: (row) => row.phoneNumber || '-'
    },
    {
      renderName: 'email',
      headerName: 'Email ID',
      minWidth: 200,
      renderCell: (row) => row.email || '-'
    },
    {
      renderName: 'isActive',
      headerName: 'Activity Status',
      minWidth: 120,
      renderCell: (row) => (
        <ThemedChip variant={row.isActive ? 'success' : 'error'} size="small">
          {row.isActive ? 'Active' : 'Inactive'}
        </ThemedChip>
      )
    },
    // TODO: Re-enable action column later
    // {
    //   renderName: 'action',
    //   headerName: 'Action',
    //   minWidth: 80,
    //   align: 'center',
    //   renderCell: (row) => (
    //     <IconButton
    //       size="small"
    //       onClick={() => {
    //         // Handle edit user
    //         console.log('Edit user:', row);
    //       }}
    //       sx={{ color: themeMode === 'dark' ? '#ffffff' : '#000000' }}
    //     >
    //       <EditIcon fontSize="small" />
    //     </IconButton>
    //   ),
    // },
  ];

  // Provider table columns for StickyFlexTable
  const providerColumns = [
    {
      renderName: 'fullName',
      headerName: 'Name',
      minWidth: 150
    },
    {
      renderName: 'npi',
      headerName: 'NPI',
      minWidth: 120,
      renderCell: (row) => row.npi || '-'
    },
    {
      renderName: 'specialties',
      headerName: 'Specialties',
      minWidth: 200,
      renderCell: (row) => {
        if (Array.isArray(row.specialties) && row.specialties.length > 0) {
          const displaySpecialties = row.specialties.slice(0, 2);
          const remainingCount = row.specialties.length - 2;
          
          return (
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {displaySpecialties.map(specialty => (
                <ThemedChip key={specialty} size="small">
                  {specialty}
                </ThemedChip>
              ))}
              {remainingCount > 0 && (
                <ThemedChip variant="info" size="small">
                  +{remainingCount} more
                </ThemedChip>
              )}
            </div>
          );
        }
        return row.specialties ? (
          <ThemedChip size="small">
            {row.specialties}
          </ThemedChip>
        ) : '-';
      }
    },
    {
      renderName: 'isActive',
      headerName: 'Activity Status',
      minWidth: 120,
      renderCell: (row) => (
        <ThemedChip variant={row.isActive ? 'success' : 'error'} size="small">
          {row.isActive ? 'Active' : 'Inactive'}
        </ThemedChip>
      )
    },
    // TODO: Re-enable action column later
    // {
    //   renderName: 'action',
    //   headerName: 'Action',
    //   minWidth: 80,
    //   align: 'center',
    //   renderCell: (row) => (
    //     <IconButton
    //       size="small"
    //       onClick={() => {
    //         // Handle edit provider
    //         console.log('Edit provider:', row);
    //       }}
    //       sx={{ color: themeMode === 'dark' ? '#ffffff' : '#000000' }}
    //     >
    //       <EditIcon fontSize="small" />
    //     </IconButton>
    //   ),
    // },
  ];

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!department) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Empty description="Department not found" />
      </div>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <div style={{
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        {/* Title Section with Back Arrow */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
          <IconButton
            onClick={onClose}
            sx={{
              color: themeMode === 'dark' ? '#ffffff' : '#000000',
              marginTop: '0.25rem'
            }}
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>
          
          <div>
            <Title 
              level={2} 
              style={{ 
                margin: '0 0 0.5rem 0',
                color: themeMode === 'dark' ? '#ffffff' : '#000000'
              }}
            >
              {department.name || 'Unknown Department'}
            </Title>
            
            <Text 
              style={{
                fontSize: '0.875rem',
                color: themeMode === 'dark' ? '#a0a0a0' : '#666666'
              }}
            >
              Facility: {facility?.facilityName || 'Unknown Facility'}
            </Text>
          </div>
        </div>

        {/* Tabs and Add User Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Tabs 
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#e63e2b', // VoiceCare orange
              },
              '& .MuiTab-root': {
                color: themeMode === 'dark' ? '#ffffff' : '#000000',
                '&.Mui-selected': {
                  color: '#e63e2b', // VoiceCare orange
                },
              },
            }}
          >
            <Tab 
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Users 
                </span>
              }
              sx={{ 
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 120,
              }} 
            />
            <Tab 
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Providers 
                </span>
              }
              sx={{ 
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 120,
              }} 
            />
          </Tabs>
          
          {/* Add User/Provider Button - show based on active tab */}
          {activeTab === 0 && (
            <button
              onClick={handleAddStaffClick}
              disabled={!department?._id || !facilityId}
              style={{
                backgroundColor: '#e63e2b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: department?._id && facilityId ? 'pointer' : 'not-allowed',
                opacity: department?._id && facilityId ? 1 : 0.5,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (department?._id && facilityId) {
                  e.target.style.backgroundColor = '#d73527';
                }
              }}
              onMouseLeave={(e) => {
                if (department?._id && facilityId) {
                  e.target.style.backgroundColor = '#e63e2b';
                }
              }}
            >
              + Add User
            </button>
          )}
          
          {/* Add Provider Button - only show when on Providers tab */}
          {activeTab === 1 && (
            <button
              onClick={handleAddProviderClick}
              disabled={!department?._id || !facilityId}
              style={{
                backgroundColor: '#e63e2b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: department?._id && facilityId ? 'pointer' : 'not-allowed',
                opacity: department?._id && facilityId ? 1 : 0.5,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (department?._id && facilityId) {
                  e.target.style.backgroundColor = '#d73527';
                }
              }}
              onMouseLeave={(e) => {
                if (department?._id && facilityId) {
                  e.target.style.backgroundColor = '#e63e2b';
                }
              }}
            >
              + Add Provider
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
        {activeTab === 0 && (
          // Users Tab
          <div>
            {dataLoading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '40vh',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <LogoLoader />
                </div>
                <Text style={{
                  fontSize: '0.875rem',
                  color: themeMode === 'dark' ? '#ffffff' : '#666666',
                  textAlign: 'center'
                }}>
                  Loading department users...
                </Text>
              </div>
            ) : staff.length > 0 ? (
              <StickyFlexTable
                columns={userColumns}
                rows={staff}
                loading={false}
                page={0}
                rowsPerPage={staff.length}
                onPageChange={() => {}}
                onRowsPerPageChange={() => {}}
                noCustomizeColumn={true}
                containerHeight="60vh"
              />
            ) : (
              <Empty 
                description="No users assigned to this department"
                style={{ margin: '2rem 0' }}
              />
            )}
          </div>
        )}

        {activeTab === 1 && (
          // Providers Tab
          <div>
            {dataLoading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '40vh',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <LogoLoader />
                </div>
                <Text style={{
                  fontSize: '0.875rem',
                  color: themeMode === 'dark' ? '#ffffff' : '#666666',
                  textAlign: 'center'
                }}>
                  Loading department providers...
                </Text>
              </div>
            ) : providers.length > 0 ? (
              <StickyFlexTable
                columns={providerColumns}
                rows={providers}
                loading={false}
                page={0}
                rowsPerPage={providers.length}
                onPageChange={() => {}}
                onRowsPerPageChange={() => {}}
                noCustomizeColumn={true}
                containerHeight="60vh"
              />
            ) : (
              <Empty 
                description="No providers assigned to this department"
                style={{ margin: '2rem 0' }}
              />
            )}
          </div>
        )}

      {/* Staff Selection Modal */}
      <StaffSelectionModal
        open={staffModalOpen}
        departmentId={department?._id}
        facilityId={facilityId}
        departmentName={department?.name}
        facilityName={facility?.facilityName}
        currentStaffIds={currentStaffIds}
        onClose={handleStaffModalClose}
        onSave={handleStaffModalSave}
      />

      {/* Provider Selection Modal */}
      <ProviderSelectionModal
        open={providerModalOpen}
        departmentId={department?._id}
        facilityId={facilityId}
        departmentName={department?.name}
        facilityName={facility?.facilityName}
        currentProviderIds={currentProviderIds}
        onClose={handleProviderModalClose}
        onSave={handleProviderModalSave}
      />
    </Box>
  );
};

export default DepartmentDetailsTabbed;
